/**
 * SubmissionSourceBadge
 * 
 * Read-only visibility hint for the Pending Approval queue + Timesheet drawer.
 * Tells coordinators at-a-glance HOW a timesheet entered the approval queue:
 *   - AUTO_SUBMITTED_CLOCKOUT  → green  "Auto-submitted"
 *   - ADMIN_CLOSED             → amber  "Admin closed"
 *   - MANUAL_SUBMIT            → grey   "Manual submit"
 * 
 * Pure UI — does NOT change approval / billing / claims / funding logic.
 * Detection is derived from existing timesheet fields stored by the
 * timesheet engine on clock-out / admin-close:
 *   - auto_submitted_on_clockout (bool)
 *   - auto_submitted_trigger ('clock_out' | 'admin_close_missed_clockout')
 *   - status ('SUBMITTED' | 'DRAFT' | …)
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Zap, ShieldAlert, Hand } from 'lucide-react';

const SOURCE_CONFIG = {
  AUTO_SUBMITTED_CLOCKOUT: {
    label: 'Auto-submitted',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100',
    Icon: Zap,
    title: 'Submitted automatically when staff clocked out.',
  },
  ADMIN_CLOSED: {
    label: 'Admin closed',
    className: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100',
    Icon: ShieldAlert,
    title: 'Admin closed a missed clock-out and submitted on the staff member\u2019s behalf.',
  },
  MANUAL_SUBMIT: {
    label: 'Manual submit',
    className: 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-100',
    Icon: Hand,
    title: 'Staff manually clicked Submit (legacy fallback path).',
  },
};

export function detectSubmissionSource(ts) {
  if (!ts) return null;
  const trigger = ts.auto_submitted_trigger;
  const auto = ts.auto_submitted_on_clockout === true;

  if (trigger === 'admin_close_missed_clockout') return 'ADMIN_CLOSED';
  if (trigger === 'clock_out' || auto) return 'AUTO_SUBMITTED_CLOCKOUT';

  // Manual fallback — only meaningful for already-submitted timesheets.
  // Pure DRAFTs aren't submission-sourced yet, so we return null and the
  // caller hides the badge.
  const legacyStatus = ts.status;
  if (legacyStatus === 'SUBMITTED') return 'MANUAL_SUBMIT';
  return null;
}

export function SubmissionSourceBadge({ timesheet, className = '', showIcon = true, size = 'xs' }) {
  const source = detectSubmissionSource(timesheet);
  if (!source) return null;
  const cfg = SOURCE_CONFIG[source];
  const { Icon } = cfg;
  const sizeClass = size === 'sm' ? 'text-sm py-0.5 px-2' : 'text-xs py-0.5 px-1.5';
  return (
    <Badge
      variant="outline"
      className={`${cfg.className} ${sizeClass} font-medium gap-1 ${className}`}
      title={cfg.title}
      data-testid={`submission-source-badge-${source.toLowerCase()}`}
    >
      {showIcon && <Icon className="h-3 w-3" />}
      {cfg.label}
    </Badge>
  );
}

export default SubmissionSourceBadge;
